import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { format, formatDistanceToNow } from 'date-fns';
import { useLang } from '../hooks/useLang';
import './SenderProfile.css';

const CATEGORY_LABELS = {
  trustworthiness: '🔒 Trustworthiness',
  communication: '💬 Communication',
  reliability: '🤝 Reliability',
  item_declaration: '📋 Item declaration',
};

function StatCard({ value, label }) {
  return (
    <div className="sp-stat">
      <div className="sp-stat-value">{value}</div>
      <div className="sp-stat-label">{label}</div>
    </div>
  );
}

export default function SenderProfile() {
  const { id } = useParams();
  const { user, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchAll();
  }, [id, user, authLoading]);

  async function fetchAll() {
    setLoading(true);
    const [profileRes, reviewsRes, bookingsRes] = await Promise.all([
      supabase.from('profiles').select('*').eq('id', id).single(),
      supabase.from('reviews')
        .select('*')
        .eq('reviewee_id', id)
        .eq('reviewer_role', 'transporter')
        .order('created_at', { ascending: false }),
      supabase.from('bookings')
        .select('status')
        .eq('sender_id', id),
    ]);

    const p = profileRes.data;

    if (!p || p.role === 'transporter') {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setProfile(p);

    const reviewData = reviewsRes.data || [];
    setReviews(reviewData);

    const avgRating = reviewData.length > 0
      ? reviewData.reduce((sum, r) => sum + r.rating, 0) / reviewData.length
      : null;

    const categoryAvgs = {};
    Object.keys(CATEGORY_LABELS).forEach(key => {
      const vals = reviewData.filter(r => r[key]).map(r => r[key]);
      if (vals.length > 0) {
        categoryAvgs[key] = (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1);
      }
    });

    const allBookings = bookingsRes.data || [];
    const totalBookings = allBookings.length;
    const completedBookings = allBookings.filter(b => b.status === 'delivered').length;

    setStats({ totalBookings, completedBookings, reviewCount: reviewData.length, avgRating, categoryAvgs });
    setLoading(false);
  }

  if (authLoading || loading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (notFound) return (
    <div className="page">
      <div className="container" style={{ textAlign: 'center', paddingTop: '80px' }}>
        <div style={{ fontSize: '48px' }}>🚫</div>
        <h2 style={{ margin: '16px 0 8px' }}>Profile not available</h2>
        <p style={{ color: 'var(--olive)' }}>This user does not have a public sender profile.</p>
        <Link to="/search" className="btn btn-primary" style={{ marginTop: '24px', display: 'inline-block' }}>Back to search</Link>
      </div>
    </div>
  );

  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));
  const memberSince = profile.created_at ? format(new Date(profile.created_at), 'MMMM yyyy') : null;

  return (
    <div className="page sp-page">
      <div className="container">
        <button className="back-link" onClick={() => navigate(-1)}>← Back</button>

        {/* HERO */}
        <div className="card sp-hero">
          <div className="sp-hero-left">
            <div className="sp-avatar">{profile.full_name?.[0] || '?'}</div>
            <div className="sp-hero-info">
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <h1 className="sp-name">{profile.full_name}</h1>
                {profile.is_verified && <span className="badge badge-green">✓ Verified</span>}
                <span className="badge badge-gray">📦 Sender</span>
              </div>
              {profile.city && (
                <div style={{ color: 'var(--olive)', fontSize: '14px', marginTop: '4px' }}>📍 {profile.city}, Germany</div>
              )}
              {memberSince && (
                <div style={{ color: 'var(--olive)', fontSize: '13px', marginTop: '4px' }}>🗓 Member since {memberSince}</div>
              )}
              {stats?.avgRating != null ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                  <span style={{ color: '#F5A623', fontSize: '18px' }}>{stars(stats.avgRating)}</span>
                  <span style={{ fontWeight: '700', fontSize: '16px' }}>{stats.avgRating.toFixed(1)}</span>
                  <span style={{ color: 'var(--olive)', fontSize: '13px' }}>({stats.reviewCount} review{stats.reviewCount !== 1 ? 's' : ''} from transporters)</span>
                </div>
              ) : (
                <div style={{ color: 'var(--olive)', fontSize: '13px', marginTop: '8px' }}>No reviews yet</div>
              )}
              {profile.bio && (
                <p style={{ marginTop: '12px', fontSize: '14px', color: 'rgba(245,237,216,0.7)', lineHeight: '1.6', maxWidth: '500px' }}>
                  {profile.bio}
                </p>
              )}
            </div>
          </div>

          {profile.role === 'both' && (
            <Link to={`/transporter/${id}`} className="btn btn-ghost sp-switch-btn">
              View as Transporter →
            </Link>
          )}
        </div>

        {/* STATS */}
        {stats && (
          <div className="sp-stats">
            <StatCard value={stats.completedBookings} label={t('senderProfile.completedShipments')} />
            <StatCard value={stats.totalBookings} label={t('senderProfile.totalBookings')} />
            <StatCard value={stats.reviewCount} label={t('senderProfile.reviews')} />
            <StatCard value={stats.avgRating != null ? stats.avgRating.toFixed(1) : '—'} label={t('senderProfile.avgRating')} />
          </div>
        )}

        <div className="sp-layout">
          <div className="sp-left">
            {stats?.categoryAvgs && Object.keys(stats.categoryAvgs).length > 0 && (
              <div className="card sp-categories">
                <h3>{t('senderProfile.ratingBreakdown')}</h3>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) =>
                  stats.categoryAvgs[key] ? (
                    <div key={key} className="sp-cat-row">
                      <span className="sp-cat-label">{label}</span>
                      <div className="sp-cat-bar-wrap">
                        <div className="sp-cat-bar">
                          <div className="sp-cat-fill" style={{ width: `${(stats.categoryAvgs[key] / 5) * 100}%` }} />
                        </div>
                        <span className="sp-cat-val">{stats.categoryAvgs[key]}</span>
                      </div>
                    </div>
                  ) : null
                )}
              </div>
            )}

            <div className="card" style={{ marginTop: '16px' }}>
              <h3 style={{ marginBottom: '20px' }}>Reviews from transporters ({reviews.length})</h3>
              {reviews.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px', color: 'var(--olive)', fontSize: '14px' }}>
                  No reviews yet.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  {reviews.map(r => (
                    <div key={r.id} style={{ paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: 'white', flexShrink: 0 }}>✓</div>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Verified transporter</div>
                            <div style={{ fontSize: '11px', color: 'var(--olive)' }}>
                              {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                            </div>
                          </div>
                        </div>
                        <div>
                          <span style={{ color: '#F5A623', fontSize: '14px' }}>{stars(r.rating)}</span>
                          <span style={{ fontSize: '12px', color: 'var(--olive)', marginLeft: '6px' }}>{r.rating}/5</span>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', margin: '8px 0' }}>
                        {Object.entries(CATEGORY_LABELS).map(([key, label]) =>
                          r[key] ? (
                            <div key={key} style={{ fontSize: '11px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '3px 8px' }}>
                              {label} <span style={{ color: '#F5A623' }}>{'★'.repeat(r[key])}</span>
                            </div>
                          ) : null
                        )}
                      </div>
                      {r.comment && (
                        <p style={{ fontSize: '14px', color: 'rgba(245,237,216,0.7)', margin: '8px 0 0', lineHeight: '1.5' }}>
                          "{r.comment}"
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="sp-right">
            <div className="card">
              <h3 style={{ marginBottom: '16px' }}>About this sender</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px' }}>
                <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>🔒 Trustworthiness</div>
                  <div style={{ color: 'var(--olive)', fontSize: '13px' }}>
                    {stats?.categoryAvgs?.trustworthiness
                      ? `Rated ${stats.categoryAvgs.trustworthiness}/5 by transporters`
                      : 'Not yet rated'}
                  </div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>📋 Item declaration</div>
                  <div style={{ color: 'var(--olive)', fontSize: '13px' }}>
                    {stats?.categoryAvgs?.item_declaration
                      ? `Rated ${stats.categoryAvgs.item_declaration}/5 for accuracy`
                      : 'Not yet rated'}
                  </div>
                </div>
                <div style={{ padding: '12px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px' }}>
                  <div style={{ fontWeight: '600', marginBottom: '4px' }}>📦 Shipments</div>
                  <div style={{ color: 'var(--olive)', fontSize: '13px' }}>
                    {stats?.completedBookings} completed out of {stats?.totalBookings} total
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}